import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import { 
  FaTimes, FaExclamationCircle, FaCheckCircle, FaFileAlt, FaUser, FaClipboardList, FaSpinner
} from "react-icons/fa";
import { Upload } from "lucide-react";
import { createRisk } from "../../api/riskApi";
import UploadDocumentModal from "../../components/UploadDocumentModal";
import { useAuth } from "../../context/AuthContext"; // ✅ ADD THIS

const API_URL = "https://ims.holdthechild.net";

export default function RiskFormModal({ isOpen, onClose, onRiskAdded }) {
  // ✅ ADD THIS: Get current user
  const { user } = useAuth();
  const currentStaffId = user?.id || null;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    category: "Operational",
    causes: "",
    consequences: "",
    likelihood: "Low",
    impact: "Low",
    rating: "Medium",
    riskOwner: "",
    mitigation: "",
    contingencyPlan: "",
    comments: "",
    status: "Open",
    treatments: [],
  });
  
  const [staffList, setStaffList] = useState([]);
  const [statusMsg, setStatusMsg] = useState({ type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const fetchStaff = async () => {
      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await axios.get(`${API_URL}/api/staff`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setStaffList(res.data.data || []);
      } catch (err) {
        console.error("Failed to fetch staff:", err);
      }
    };
    fetchStaff();
  }, []);

  const staffOptions = staffList.map((staff) => ({
    value: staff._id,
    label: `${staff.firstName} ${staff.lastName}`,
  }));

  const categories = ["Operational", "Compliance", "Financial", "Technical", "Other"];
  const statuses = ["Open", "In Progress", "Resolved", "Closed"];
  const treatmentStatuses = ["Not Started", "In Progress", "Completed"];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addTreatment = () => {
    setFormData(prev => ({
      ...prev,
      treatments: [...prev.treatments, { action: "", dueDate: "", status: "Not Started" }],
    }));
  };

  const handleTreatmentChange = (idx, field, value) => {
    const newTreatments = [...formData.treatments];
    newTreatments[idx][field] = value;
    setFormData(prev => ({ ...prev, treatments: newTreatments }));
  };

  const removeTreatment = (idx) => {
    setFormData(prev => ({ ...prev, treatments: prev.treatments.filter((_, i) => i !== idx) }));
  };

  const handleUploadSuccess = (documents) => {
    console.log("📄 Documents uploaded for risk:", documents); // ✅ DEBUG
    setUploadedDocuments(documents);
    setIsUploadModalOpen(false);
  };

  const removeDocument = (index) => {
    setUploadedDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setStatusMsg({ type: "", message: "" });

    try {
      // ✅ FIXED: Create the risk with proper document references
      const riskData = {
        ...formData,
        createdBy: currentStaffId, // ✅ ADD: Associate with current user
        documents: uploadedDocuments.map(doc => ({
          documentId: doc._id, // ✅ Reference the actual document ID
          fileUrl: doc.fileUrl,
          originalName: doc.originalName,
          category: doc.category || "Risks", // ✅ FIXED: Use "Risks" category
          fileType: doc.originalName.split('.').pop().toLowerCase()
        }))
      };

      console.log("🚀 Creating risk with data:", riskData); // ✅ DEBUG

      const newRisk = await createRisk(riskData);

      if (onRiskAdded) onRiskAdded(newRisk);
      
      // ✅ Show success message before closing
      setStatusMsg({ type: "success", message: "Risk created successfully!" });
      
      setTimeout(() => {
        onClose();
        // Reset form
        setFormData({
          title: "",
          description: "",
          category: "Operational",
          causes: "",
          consequences: "",
          likelihood: "Low",
          impact: "Low",
          rating: "Medium",
          riskOwner: "",
          mitigation: "",
          contingencyPlan: "",
          comments: "",
          status: "Open",
          treatments: [],
        });
        setUploadedDocuments([]);
        setStatusMsg({ type: "", message: "" });
      }, 1500);

    } catch (error) {
      console.error("❌ Risk creation error:", error);
      setStatusMsg({ type: "error", message: "Failed to create risk. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        {submitting && (
          <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/70">
            <div className="bg-white rounded-xl p-8 max-w-sm mx-4 text-center shadow-2xl">
              <FaSpinner className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-800 mb-2">Submitting Risk</h3>
              <p className="text-gray-600">Please wait while we process the risk entry...</p>
            </div>
          </div>
        )}

        <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col overflow-hidden transform transition-all duration-300 ease-out">

          {/* Header */}
          <div className="flex-shrink-0 bg-gradient-to-r from-orange-500 to-orange-600 border-b border-orange-50">
            <div className="flex items-center justify-between px-6 py-5">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl shadow-sm">
                  <FaClipboardList className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Add New Risk</h2>
                  <p className="text-orange-100 mt-1">Risk Management System</p>
                </div>
              </div>
              <button onClick={onClose} className="p-2 rounded-xl transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white hover:shadow-sm">
                <FaTimes className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex flex-col flex-grow overflow-hidden">
            <div className="flex-grow px-6 py-5 overflow-y-auto space-y-6">

              {/* Status Message */}
              {statusMsg.message && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${statusMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {statusMsg.type === 'success' ? <FaCheckCircle className="text-green-500 flex-shrink-0" /> : <FaExclamationCircle className="text-red-500 flex-shrink-0" />}
                  <span>{statusMsg.message}</span>
                </div>
              )}

              {/* Key Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input type="text" name="title" value={formData.title} onChange={handleChange} required
                    className="w-full pl-3 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select name="category" value={formData.category} onChange={handleChange}
                    className="w-full pl-3 pr-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows={3}
                  className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"/>
              </div>

              {/* Likelihood, Impact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Likelihood</label>
                  <select name="likelihood" value={formData.likelihood} onChange={handleChange} className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    <option>Low</option><option>Medium</option><option>High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Impact</label>
                  <select name="impact" value={formData.impact} onChange={handleChange} className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500">
                    <option>Low</option><option>Medium</option><option>High</option>
                  </select>
                </div>
              </div>

              {/* Causes & Consequences */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Causes</label>
                  <textarea name="causes" value={formData.causes} onChange={handleChange} rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"/>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Consequences</label>
                  <textarea name="consequences" value={formData.consequences} onChange={handleChange} rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"/>
                </div>
              </div>

              {/* Mitigation & Contingency Plan */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mitigation</label>
                  <textarea name="mitigation" value={formData.mitigation} onChange={handleChange} rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"/>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl shadow-sm">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contingency Plan</label>
                  <textarea name="contingencyPlan" value={formData.contingencyPlan} onChange={handleChange} rows={3}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"/>
                </div>
              </div>

              {/* Risk Owner */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Risk Owner</label>
                <Select options={staffOptions} value={staffOptions.find(s => s.value === formData.riskOwner) || null} onChange={(selected) => setFormData(prev => ({...prev, riskOwner: selected ? selected.value : ""}))} placeholder="Select Risk Owner..." isClearable className="react-select-container" classNamePrefix="react-select"/>
              </div>

              {/* Treatments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Treatments / Actions</label>
                <div className="space-y-2">
                  {formData.treatments.map((t, idx) => (
                    <div key={idx} className="grid grid-cols-3 gap-2 items-center border border-gray-300 p-3 rounded-lg bg-gray-50 shadow-sm">
                      <input type="text" placeholder="Action" value={t.action} onChange={(e)=>handleTreatmentChange(idx,"action",e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm w-full"/>
                      <input type="date" value={t.dueDate} onChange={(e)=>handleTreatmentChange(idx,"dueDate",e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm w-full"/>
                      <select value={t.status} onChange={(e)=>handleTreatmentChange(idx,"status",e.target.value)} className="border border-gray-300 rounded px-2 py-1 text-sm w-full">
                        {treatmentStatuses.map(s => <option key={s}>{s}</option>)}
                      </select>
                      <button type="button" onClick={()=>removeTreatment(idx)} className="text-red-500 text-xs ml-2">✕</button>
                    </div>
                  ))}
                  <button type="button" onClick={addTreatment} className="mt-2 px-3 py-1 bg-orange-500 text-white rounded hover:bg-orange-600">+ Add Treatment</button>
                </div>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Comments</label>
                <textarea name="comments" value={formData.comments} onChange={handleChange} rows={2} className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"/>
              </div>

              {/* Upload Documents Section */}
              <div className="bg-gray-50 p-4 rounded-xl shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <label className="block text-sm font-medium text-gray-700">Risk Documents</label>
                  <button
                    type="button"
                    onClick={() => setIsUploadModalOpen(true)}
                    className="inline-flex items-center px-4 py-2 bg-orange-600 text-white text-sm font-medium rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Documents
                  </button>
                </div>

                {/* Display uploaded documents */}
                {uploadedDocuments.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 mb-2">{uploadedDocuments.length} document(s) uploaded:</p>
                    {uploadedDocuments.map((doc, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FaFileAlt className="text-orange-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{doc.originalName}</p>
                            <p className="text-xs text-gray-500">{doc.category}</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDocument(index)}
                          className="text-red-500 hover:text-red-700 text-sm"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploadedDocuments.length === 0 && (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    No documents uploaded yet. Click "Upload Documents" to add files.
                  </div>
                )}
              </div>

            </div>

            {/* Footer */}
            <div className="flex-shrink-0 bg-gray-50 px-6 py-5 border-t border-gray-200 flex justify-end space-x-4">
              <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 text-gray-700 hover:bg-gray-200 border border-gray-300">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className={`relative px-8 py-3 rounded-xl font-semibold text-white transition-all duration-200 transform ${submitting ? 'bg-orange-400 cursor-not-allowed' : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 hover:scale-105 shadow-lg hover:shadow-xl'}`}>
                {submitting ? (<div className="flex items-center space-x-3"><FaSpinner className="w-5 h-5 animate-spin"/> <span>Submitting...</span></div>) : (<span>Save Risk</span>)}
              </button>
            </div>

          </form>
        </div>
      </div>

      {/* ✅ FIXED: Upload Modal with correct staffId and category */}
      <UploadDocumentModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        staffId={currentStaffId} // ✅ FIXED: Use current user's ID
        onUploadSuccessWithFiles={handleUploadSuccess}
        category="Risks" // ✅ FIXED: Use "Risks" to match your document repo
      />
    </>
  );
}
